import React, { useEffect, useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, ClinicalNote, LabRequest, RadiologyRequest, Prescription } from '../../types';
import { Stethoscope, FileText, FlaskConical, Scan, Pill, CheckCircle, History, X, ZoomIn } from 'lucide-react';
export const ClinicianDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters
  } = useData();
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  // Live encounter - always reads from context so lab/rad updates appear instantly
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Image zoom state
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // Modals state
  const [showLabModal, setShowLabModal] = useState(false);
  const [showRadModal, setShowRadModal] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  // Clinical Notes State
  const [notes, setNotes] = useState<Partial<ClinicalNote>>({
    presentingComplaint: '',
    historyOfPresentingComplaint: '',
    pastMedicalHistory: [],
    drugHistory: '',
    familyMedicalHistory: [],
    obgynHistory: '',
    socialHistory: {
      alcohol: '',
      smoking: '',
      occupation: '',
      mentalStatus: ''
    },
    reviewOfSystems: {
      rs: '',
      cvs: '',
      cns: '',
      git: '',
      gut: '',
      mss: '',
      is: ''
    },
    onExamination: '',
    significantFindings: [],
    workingDiagnosis: []
  });
  // Lab Request State
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [otherTests, setOtherTests] = useState('');
  // Rad Request State
  const [scanType, setScanType] = useState('');
  // Rx State
  const [rxForm, setRxForm] = useState({
    drugName: '',
    dose: '',
    route: '',
    frequency: '',
    days: ''
  });
  const pendingEncounters = encounters.filter(e => e.stage === 'CLINICIAN' || e.stage === 'INVESTIGATIONS' && !e.pendingLab && !e.pendingRadiology);
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    if (encounter.clinicalNote) {
      setNotes(encounter.clinicalNote);
    } else {
      setNotes({
        presentingComplaint: '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: [],
        drugHistory: '',
        familyMedicalHistory: [],
        obgynHistory: '',
        socialHistory: {
          alcohol: '',
          smoking: '',
          occupation: '',
          mentalStatus: ''
        },
        reviewOfSystems: {
          rs: '',
          cvs: '',
          cns: '',
          git: '',
          gut: '',
          mss: '',
          is: ''
        },
        onExamination: '',
        significantFindings: [],
        workingDiagnosis: []
      });
    }
    // Move encounter back to CLINICIAN stage if returning from investigations
    if (encounter.stage === 'INVESTIGATIONS') {
      updateEncounter(encounter.id, {
        stage: 'CLINICIAN'
      });
    }
  };
  const handleSaveNotes = () => {
    if (!selectedEncounter || !currentUser) return;
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      lastAttendingClinician: currentUser.fullName
    });
    alert('Clinical notes saved successfully.');
  };
  const handleRequestLab = () => {
    if (!selectedEncounter || !currentUser || selectedTests.length === 0 && !otherTests) return;
    const newLab: LabRequest = {
      id: `lab-${Date.now()}`,
      tests: selectedTests,
      otherTests,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      labRequests: [...selectedEncounter.labRequests, newLab],
      stage: 'INVESTIGATIONS',
      pendingLab: true
    });
    setShowLabModal(false);
    setSelectedTests([]);
    setOtherTests('');
    alert('Laboratory tests requested.');
  };
  const handleRequestRad = () => {
    if (!selectedEncounter || !currentUser || !scanType) return;
    const newRad: RadiologyRequest = {
      id: `rad-${Date.now()}`,
      scanType,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: [...selectedEncounter.radiologyRequests, newRad],
      stage: 'INVESTIGATIONS',
      pendingRadiology: true
    });
    setShowRadModal(false);
    setScanType('');
    alert('Radiology scan requested.');
  };
  const handleAddRx = () => {
    if (!selectedEncounter || !currentUser || !rxForm.drugName) return;
    const newRx: Prescription = {
      id: `rx-${Date.now()}`,
      ...rxForm,
      status: 'Pending',
      prescribedAt: new Date().toISOString(),
      prescribedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      prescriptions: [...selectedEncounter.prescriptions, newRx]
    });
    setRxForm({
      drugName: '',
      dose: '',
      route: '',
      frequency: '',
      days: ''
    });
  };
  const handleCompleteVisit = () => {
    if (!selectedEncounter || !currentUser) return;
    // Save notes one last time
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    const hasPrescriptions = selectedEncounter.prescriptions.length > 0;
    const hasPendingDispense = selectedEncounter.prescriptions.some(rx => rx.status === 'Pending');
    // If prescriptions exist and none have been dispensed yet, send to pharmacy.
    // If pharmacy has already processed them (returned to clinician), complete the visit.
    const sendToPharmacy = hasPrescriptions && hasPendingDispense;
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      lastAttendingClinician: currentUser.fullName,
      stage: sendToPharmacy ? 'PHARMACY' : 'COMPLETED',
      completedAt: sendToPharmacy ? undefined : new Date().toISOString()
    });
    alert(sendToPharmacy ? 'Visit saved. Patient forwarded to Pharmacy.' : 'Visit completed successfully.');
    setSelectedEncounterId(null);
  };
  const handleSendToPharmacy = () => {
    if (!selectedEncounter || !currentUser) return;
    const pendingRx = selectedEncounter.prescriptions.filter(rx => rx.status === 'Pending');
    if (pendingRx.length === 0) {
      alert('No pending prescriptions to send to pharmacy.');
      return;
    }
    // Save current notes
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      lastAttendingClinician: currentUser.fullName,
      stage: 'PHARMACY'
    });
    alert(`Prescription sent to Pharmacy (${pendingRx.length} drug${pendingRx.length > 1 ? 's' : ''}).`);
    setSelectedEncounterId(null);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-470">Patient not found</div>;
    return <div className="space-y-6 pb-20" data-id="element-471">
        {/* Sticky Action Bar */}
        <div className="flex justify-between items-center sticky top-0 bg-gray-100 z-10 py-4 border-b border-gray-200" data-id="element-472">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-473">Consultation</h1>
          <div className="flex space-x-2" data-id="element-474">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-475">
              Close File
            </Button>
            <Button variant="primary" onClick={handleSaveNotes} data-id="element-476">
              Save Draft
            </Button>
            <Button variant="success" className="bg-green-600 text-white hover:bg-green-700" onClick={handleCompleteVisit} data-id="element-477">
              <CheckCircle className="w-4 h-4 mr-2" data-id="element-478" /> Complete Visit
            </Button>
          </div>
        </div>

        {/* Medical Form */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-479">
          {/* ═══ FORM HEADER ═══ */}
          <div className="flex justify-between items-start mb-6" data-id="element-480">
            <div className="flex items-center" data-id="element-481">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-482" />
              <div data-id="element-483">
                <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-484">
                  BMED Medical Centre
                </h1>
                <p className="text-sm italic text-gray-600" data-id="element-485">
                  Maternity Care Made Better
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-486">
              <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-487">
                Confidential
              </p>
              <p className="text-sm" data-id="element-488">
                <span className="font-semibold" data-id="element-489">Date:</span>{' '}
                {new Date(selectedEncounter.startedAt).toLocaleDateString()}
              </p>
              <p className="text-sm" data-id="element-490">
                <span className="font-semibold" data-id="element-491">File No:</span>{' '}
                {patient.bmcFileNumber}
              </p>
            </div>
          </div>

          {/* ═══ PERSONAL DETAILS ═══ */}
          <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-492">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-493">
              <div className="col-span-1 font-semibold" data-id="element-494">1.</div>
              <div className="col-span-6" data-id="element-495">
                <span className="font-semibold" data-id="element-496">Full Names:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-3" data-id="element-497">
                <span className="font-semibold" data-id="element-498">Sex:</span> {patient.sex}
              </div>
              <div className="col-span-2" data-id="element-499">
                <span className="font-semibold" data-id="element-500">Age:</span> {patient.age}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-501">
              <div className="col-span-1 font-semibold" data-id="element-502">2.</div>
              <div className="col-span-11" data-id="element-503">
                <span className="font-semibold" data-id="element-504">Marital Status:</span>{' '}
                {patient.maritalStatus}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-505">
              <div className="col-span-1 font-semibold" data-id="element-506">3.</div>
              <div className="col-span-7" data-id="element-507">
                <span className="font-semibold" data-id="element-508">Home Address:</span>{' '}
                {patient.address}
              </div>
              <div className="col-span-4" data-id="element-509">
                <span className="font-semibold" data-id="element-510">Contact:</span>{' '}
                {patient.contact}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-511">
              <div className="col-span-1 font-semibold" data-id="element-512">4.</div>
              <div className="col-span-11" data-id="element-513">
                <span className="font-semibold" data-id="element-514">Occupation:</span>{' '}
                {patient.occupation || '_________________________'}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-515">
              <div className="col-span-1 font-semibold" data-id="element-516">5.</div>
              <div className="col-span-5" data-id="element-517">
                <span className="font-semibold" data-id="element-518">Next of Kin:</span>{' '}
                {patient.nextOfKin}
              </div>
              <div className="col-span-3" data-id="element-519">
                <span className="font-semibold" data-id="element-520">Contact:</span>{' '}
                {patient.nokContact}
              </div>
              <div className="col-span-3" data-id="element-521">
                <span className="font-semibold" data-id="element-522">Relationship:</span>{' '}
                {patient.nokRelationship}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-523">
              <div className="col-span-1 font-semibold" data-id="element-524">6.</div>
              <div className="col-span-11" data-id="element-525">
                <span className="font-semibold" data-id="element-526">Allergies:</span>{' '}
                <span className="text-red-600 font-medium" data-id="element-527">
                  {patient.allergies}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-528">
              <div className="col-span-1 font-semibold" data-id="element-529">7.</div>
              <div className="col-span-11" data-id="element-530">
                <span className="font-semibold" data-id="element-531">HIV Status:</span>{' '}
                <span className="ml-4" data-id="element-532">
                  <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-533">
                    Positive {patient.hivStatus === 'Positive' ? '✓' : ''}
                  </span>
                  <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-534">
                    Negative {patient.hivStatus === 'Negative' ? '✓' : ''}
                  </span>
                  <span className={`inline-block px-2 py-0.5 border border-black ${patient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-535">
                    Unknown {patient.hivStatus === 'Unknown' ? '✓' : ''}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* ═══ NURSING OBSERVATIONS ═══ */}
          <div className="mb-6" data-id="element-536">
            <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-537">
              Nursing Observations
            </h3>
            <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-538">
              <thead data-id="element-539">
                <tr className="bg-gray-50" data-id="element-540">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-541">
                      {n}
                    </th>)}
                </tr>
                <tr className="bg-gray-100" data-id="element-542">
                  <th className="border border-black p-1" data-id="element-543">Temp</th>
                  <th className="border border-black p-1" data-id="element-544">Pulse</th>
                  <th className="border border-black p-1" data-id="element-545">Resp</th>
                  <th className="border border-black p-1" data-id="element-546">BP</th>
                  <th className="border border-black p-1" data-id="element-547">SpO2</th>
                  <th className="border border-black p-1" data-id="element-548">Weight (Kg)</th>
                  <th className="border border-black p-1" data-id="element-549">Height</th>
                  <th className="border border-black p-1" data-id="element-550">BMI</th>
                  <th className="border border-black p-1" data-id="element-551">Pain Score</th>
                  <th className="border border-black p-1" data-id="element-552">AVPU</th>
                </tr>
              </thead>
              <tbody data-id="element-553">
                <tr data-id="element-554">
                  <td className="border border-black p-2 font-medium" data-id="element-555">
                    {selectedEncounter.vitals?.temp || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-556">
                    {selectedEncounter.vitals?.pulse || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-557">
                    {selectedEncounter.vitals?.resp || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-558">
                    {selectedEncounter.vitals?.bp || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-559">
                    {selectedEncounter.vitals?.sp02 || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-560">
                    {selectedEncounter.vitals?.weight || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-561">
                    {selectedEncounter.vitals?.height || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-562">
                    {selectedEncounter.vitals?.bmi || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-563">
                    {selectedEncounter.vitals?.painScore || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-564">
                    {selectedEncounter.vitals?.avpu || ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ═══ STAT DOSES ADMINISTERED BY NURSING ═══ */}
          {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="border-2 border-black mb-6" data-id="element-565">
                <h3 className="font-bold uppercase bg-yellow-100 p-2 border-b border-black text-center text-sm" data-id="element-566">
                  ⚡ Stat Doses Administered by Nursing
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-567">
                  <thead data-id="element-568">
                    <tr className="bg-gray-100" data-id="element-569">
                      <th className="border border-black p-1.5 w-10" data-id="element-570">No.</th>
                      <th className="border border-black p-1.5 text-left" data-id="element-571">
                        Drug Name
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-572">
                        Dose
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-573">
                        Route
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-574">
                        Time Given
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-575">
                        Nurse
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-576">
                    {selectedEncounter.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-577">
                        <td className="border border-black p-1.5 text-center" data-id="element-578">
                          {idx + 1}
                        </td>
                        <td className="border border-black p-1.5 font-medium" data-id="element-579">
                          {dose.drugName}
                        </td>
                        <td className="border border-black p-1.5" data-id="element-580">
                          {dose.dose}
                        </td>
                        <td className="border border-black p-1.5" data-id="element-581">
                          {dose.route}
                        </td>
                        <td className="border border-black p-1.5" data-id="element-582">
                          {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                        </td>
                        <td className="border border-black p-1.5 italic text-xs" data-id="element-583">
                          {dose.nurseName || ''}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

          {/* ═══ CLINICAL NOTES ═══ */}
          <div className="space-y-5 mb-6 text-sm" data-id="element-584">
            {/* PRESENTING COMPLAINT */}
            <div className="border-2 border-black" data-id="element-585">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-586">
                Presenting Complaint
              </h3>
              <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.presentingComplaint} onChange={e => setNotes({
              ...notes,
              presentingComplaint: e.target.value
            })} data-id="element-587" />
            </div>

            {/* HISTORY OF PRESENTING COMPLAINT */}
            <div className="border-2 border-black" data-id="element-588">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-589">
                History of Presenting Complaint
              </h3>
              <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.historyOfPresentingComplaint} onChange={e => setNotes({
              ...notes,
              historyOfPresentingComplaint: e.target.value
            })} data-id="element-590" />
            </div>

            {/* PAST MEDICAL HISTORY */}
            <div className="border-2 border-black" data-id="element-591">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-592">
                Past Medical History / General Co-Morbidities
              </h3>
              <table className="w-full border-collapse text-sm" data-id="element-593">
                <tbody data-id="element-594">
                  {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-595">
                      <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-596">
                        {condition}
                      </td>
                      <td className="p-2 text-center" data-id="element-597">
                        <label className="flex items-center justify-center cursor-pointer" data-id="element-598">
                          <input type="checkbox" className="mr-2 h-4 w-4" checked={notes.pastMedicalHistory?.includes(condition)} onChange={e => {
                        const current = notes.pastMedicalHistory || [];
                        if (e.target.checked) {
                          setNotes({
                            ...notes,
                            pastMedicalHistory: [...current, condition]
                          });
                        } else {
                          setNotes({
                            ...notes,
                            pastMedicalHistory: current.filter(c => c !== condition)
                          });
                        }
                      }} data-id="element-599" />
                          {notes.pastMedicalHistory?.includes(condition) ? 'Yes' : ''}
                        </label>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* DRUG HISTORY */}
            <div className="border-2 border-black" data-id="element-600">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-601">
                Drug History
              </h3>
              <textarea className="w-full p-3 min-h-[60px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.drugHistory} onChange={e => setNotes({
              ...notes,
              drugHistory: e.target.value
            })} data-id="element-602" />
              <div className="p-3 border-t border-gray-300 bg-gray-50" data-id="element-603">
                <span className="font-semibold" data-id="element-604">Any known allergy:</span>{' '}
                <span className="text-red-600 font-medium" data-id="element-605">
                  {patient.allergies}
                </span>
              </div>
            </div>

            {/* FAMILY MEDICAL HISTORY */}
            <div className="border-2 border-black" data-id="element-606">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-607">
                Family Medical History
              </h3>
              <table className="w-full border-collapse text-sm" data-id="element-608">
                <tbody data-id="element-609">
                  {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-610">
                      <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-611">
                        {condition}
                      </td>
                      <td className="p-2 text-center" data-id="element-612">
                        <label className="flex items-center justify-center cursor-pointer" data-id="element-613">
                          <input type="checkbox" className="mr-2 h-4 w-4" checked={notes.familyMedicalHistory?.includes(condition)} onChange={e => {
                        const current = notes.familyMedicalHistory || [];
                        if (e.target.checked) {
                          setNotes({
                            ...notes,
                            familyMedicalHistory: [...current, condition]
                          });
                        } else {
                          setNotes({
                            ...notes,
                            familyMedicalHistory: current.filter(c => c !== condition)
                          });
                        }
                      }} data-id="element-614" />
                          {notes.familyMedicalHistory?.includes(condition) ? 'Yes' : ''}
                        </label>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
            <div className="border-2 border-black" data-id="element-615">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-616">
                Obstetrics and Gynaecology History
              </h3>
              <textarea className="w-full p-3 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.obgynHistory} onChange={e => setNotes({
              ...notes,
              obgynHistory: e.target.value
            })} data-id="element-617" />
            </div>

            {/* SOCIAL HISTORY */}
            <div className="border-2 border-black" data-id="element-618">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-619">
                Social History
              </h3>
              <table className="w-full border-collapse text-sm" data-id="element-620">
                <tbody data-id="element-621">
                  <tr className="border-b border-gray-300" data-id="element-622">
                    <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-623">
                      Alcohol
                    </td>
                    <td className="p-2" data-id="element-624">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.alcohol || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        alcohol: e.target.value
                      }
                    })} data-id="element-625" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300" data-id="element-626">
                    <td className="border-r border-gray-300 p-2 font-medium" data-id="element-627">
                      Smoking
                    </td>
                    <td className="p-2" data-id="element-628">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.smoking || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        smoking: e.target.value
                      }
                    })} data-id="element-629" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300" data-id="element-630">
                    <td className="border-r border-gray-300 p-2 font-medium" data-id="element-631">
                      Occupation
                    </td>
                    <td className="p-2" data-id="element-632">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.occupation || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        occupation: e.target.value
                      }
                    })} data-id="element-633" />
                    </td>
                  </tr>
                  <tr data-id="element-634">
                    <td className="border-r border-gray-300 p-2 font-medium" data-id="element-635">
                      Mental Status
                    </td>
                    <td className="p-2" data-id="element-636">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.mentalStatus || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        mentalStatus: e.target.value
                      }
                    })} data-id="element-637" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* REVIEW OF SYSTEMS */}
            <div className="border-2 border-black" data-id="element-638">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-639">
                Review of Systems
              </h3>
              <div className="p-3 space-y-2" data-id="element-640">
                {[{
                key: 'rs',
                label: 'R/S:'
              }, {
                key: 'cvs',
                label: 'CVS:'
              }, {
                key: 'cns',
                label: 'CNS:'
              }, {
                key: 'git',
                label: 'GIT:'
              }, {
                key: 'gut',
                label: 'GUT:'
              }, {
                key: 'mss',
                label: 'MSS:'
              }, {
                key: 'is',
                label: 'IS:'
              }].map(system => <div key={system.key} className="flex items-center" data-id="element-641">
                    <span className="font-semibold w-16" data-id="element-642">{system.label}</span>
                    <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.reviewOfSystems?.[system.key as keyof typeof notes.reviewOfSystems] || ''} onChange={e => setNotes({
                  ...notes,
                  reviewOfSystems: {
                    ...notes.reviewOfSystems,
                    [system.key]: e.target.value
                  }
                })} data-id="element-643" />
                  </div>)}
              </div>
            </div>

            {/* ON EXAMINATION */}
            <div className="border-2 border-black" data-id="element-644">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-645">
                On Examination
              </h3>
              <textarea className="w-full p-3 min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="G/C – Stable / Ill Looking / Unconscious&#10;- Febrile / Afebrile / Lethargic&#10;- P|J|C&#10;&#10;S1  S2 : -&#10;&#10;R/S  : -&#10;&#10;P/A" value={notes.onExamination} onChange={e => setNotes({
              ...notes,
              onExamination: e.target.value
            })} data-id="element-646" />
            </div>

            {/* SUMMARY OF SIGNIFICANT FINDINGS */}
            <div className="border-2 border-black" data-id="element-647">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-648">
                Summary of Significant Findings
              </h3>
              <div className="p-3 space-y-2" data-id="element-649">
                {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className="flex items-start" data-id="element-650">
                    <span className="font-medium mr-2 mt-1" data-id="element-651">{i + 1}.</span>
                    <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.significantFindings?.[i] || ''} onChange={e => {
                  const current = notes.significantFindings || Array(8).fill('');
                  const updated = [...current];
                  updated[i] = e.target.value;
                  setNotes({
                    ...notes,
                    significantFindings: updated
                  });
                }} data-id="element-652" />
                  </div>)}
              </div>
            </div>

            {/* WORKING DIAGNOSIS */}
            <div className="border-2 border-black" data-id="element-653">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-654">
                Working Diagnosis
              </h3>
              <div className="p-3 space-y-2" data-id="element-655">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => <div key={i} className="flex items-start" data-id="element-656">
                    <span className="font-medium mr-2 mt-1" data-id="element-657">{i + 1}.</span>
                    <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.workingDiagnosis?.[i] || ''} onChange={e => {
                  const current = notes.workingDiagnosis || Array(10).fill('');
                  const updated = [...current];
                  updated[i] = e.target.value;
                  setNotes({
                    ...notes,
                    workingDiagnosis: updated
                  });
                }} data-id="element-658" />
                  </div>)}
              </div>
            </div>

            {/* INVESTIGATIONS TO BE DONE */}
            <div className="border-2 border-black" data-id="element-659">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-660">
                Investigations To Be Done
              </h3>
              <div className="p-4" data-id="element-661">
                <table className="w-full border-collapse text-sm mb-4" data-id="element-662">
                  <tbody data-id="element-663">
                    {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATINE / U&A', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['LIVER FUNCTION TEST', 'DOA 6'], ['CXR', 'RPR'], ['HIV', 'SCAN/ULTRA SOUND'], ['SPERM ANALYSIS', 'TESTOSTERONE']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-664">
                        {pair.map(test => {
                      const isOrdered = selectedEncounter.labRequests.some(lab => lab.tests.includes(test)) || test === 'SCAN/ULTRA SOUND' && selectedEncounter.radiologyRequests.length > 0;
                      return <Fragment key={test} data-id="element-665">
                              <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-666">
                                {test}
                              </td>
                              <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-667">
                                {isOrdered ? <span className="text-green-700 font-bold" data-id="element-668">
                                    ✓
                                  </span> : ''}
                              </td>
                            </Fragment>;
                    })}
                      </tr>)}
                  </tbody>
                </table>

                <div className="flex gap-3 mb-4" data-id="element-669">
                  <Button variant="outline" onClick={() => setShowLabModal(true)} className="flex-1" data-id="element-670">
                    <FlaskConical className="w-4 h-4 mr-2" data-id="element-671" /> Request Laboratory
                    Test
                  </Button>
                  <Button variant="outline" onClick={() => setShowRadModal(true)} className="flex-1 border-purple-600 text-purple-600 hover:bg-purple-50" data-id="element-672">
                    <Scan className="w-4 h-4 mr-2" data-id="element-673" /> Request Radiology Scan
                  </Button>
                </div>

                <div className="border-t border-gray-300 pt-2" data-id="element-674">
                  <span className="font-semibold" data-id="element-675">Other investigations:</span>{' '}
                  {selectedEncounter.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                </div>

                {/* Investigation Results */}
                {(selectedEncounter.labRequests.some(l => l.status === 'Completed') || selectedEncounter.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-676">
                    <h4 className="font-bold mb-3 uppercase text-center" data-id="element-677">
                      Investigation Results
                    </h4>

                    {selectedEncounter.labRequests.filter(l => l.status === 'Completed' && l.results).map(lab => <table key={lab.id} className="w-full border-collapse text-sm mb-3" data-id="element-678">
                          <tbody data-id="element-679">
                            {Object.entries(lab.results!).map(([t, r]) => <tr key={t} className="border-b border-gray-200" data-id="element-680">
                                <td className="p-1.5 font-medium w-1/3 border-r border-gray-200" data-id="element-681">
                                  {t}
                                </td>
                                <td className="p-1.5" data-id="element-682">{r}</td>
                              </tr>)}
                          </tbody>
                        </table>)}

                    {selectedEncounter.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <div key={rad.id} className="mb-4 border border-gray-300 rounded p-3" data-id="element-683">
                          <p className="font-bold mb-1" data-id="element-684">{rad.scanType}</p>
                          {rad.radiographerName && <p className="text-xs text-gray-500 mb-2" data-id="element-685">
                              Radiographer: {rad.radiographerName}
                            </p>}
                          {rad.report && <div className="mb-2" data-id="element-686">
                              <span className="font-semibold" data-id="element-687">Report:</span>
                              <p className="whitespace-pre-wrap mt-1" data-id="element-688">
                                {rad.report}
                              </p>
                            </div>}
                          {rad.images && rad.images.length > 0 && <div data-id="element-689">
                              <span className="font-semibold" data-id="element-690">
                                Scan Images:
                              </span>
                              <div className="flex gap-3 mt-2 overflow-x-auto" data-id="element-691">
                                {rad.images.map((img, idx) => <div key={idx} className="relative group cursor-pointer flex-shrink-0" onClick={() => setZoomImage(img)} data-id="element-692">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="h-28 w-28 object-cover rounded border-2 border-gray-300 group-hover:border-blue-500 transition-colors" data-id="element-693" />
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded flex items-center justify-center" data-id="element-694">
                                      <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-695" />
                                    </div>
                                  </div>)}
                              </div>
                            </div>}
                        </div>)}
                  </div>}
              </div>
            </div>

            {/* DRUG TREATMENT / PRESCRIPTION */}
            <div className="border-2 border-black" data-id="element-696">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-697">
                Drug Treatment / Prescription
              </h3>
              <table className="w-full border-collapse text-sm text-center" data-id="element-698">
                <thead data-id="element-699">
                  <tr className="bg-gray-100" data-id="element-700">
                    <th className="border border-black p-1.5 w-10" data-id="element-701">No.</th>
                    <th className="border border-black p-1.5 text-left" data-id="element-702">
                      Drug Name
                    </th>
                    <th className="border border-black p-1.5" data-id="element-703">Dose</th>
                    <th className="border border-black p-1.5" data-id="element-704">Route</th>
                    <th className="border border-black p-1.5" data-id="element-705">Frequency</th>
                    <th className="border border-black p-1.5" data-id="element-706">No. of Days</th>
                    <th className="border border-black p-1.5" data-id="element-707">Signature</th>
                  </tr>
                </thead>
                <tbody data-id="element-708">
                  {selectedEncounter.prescriptions.length > 0 ? selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={rx.status === 'Dispensed' ? 'bg-green-50' : rx.status === 'Not Dispensed' ? 'bg-red-50' : ''} data-id="element-709">
                          <td className="border border-black p-1.5" data-id="element-710">{i + 1}</td>
                          <td className="border border-black p-1.5 text-left font-medium" data-id="element-711">
                            {rx.drugName}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-712">
                            {rx.dose}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-713">
                            {rx.route}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-714">
                            {rx.frequency}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-715">
                            {rx.days}
                          </td>
                          <td className="border border-black p-1.5 text-xs" data-id="element-716">
                            {rx.status === 'Dispensed' && <span className="text-green-700 font-bold" data-id="element-717">
                                ✓ Dispensed
                              </span>}
                            {rx.status === 'Not Dispensed' && <span className="text-red-700 font-bold" data-id="element-718">
                                ✗ Not Available
                              </span>}
                            {rx.status === 'Pending' && <span className="italic text-gray-500" data-id="element-719">
                                Pending
                              </span>}
                            {rx.pharmacistName && <div className="text-gray-500 italic mt-0.5" data-id="element-720">
                                {rx.pharmacistName}
                              </div>}
                          </td>
                        </tr>) : [1, 2, 3, 4, 5, 6, 7].map(i => <tr key={i} data-id="element-721">
                          <td className="border border-black p-3" data-id="element-722">{i}</td>
                          <td className="border border-black p-3" data-id="element-723"></td>
                          <td className="border border-black p-3" data-id="element-724"></td>
                          <td className="border border-black p-3" data-id="element-725"></td>
                          <td className="border border-black p-3" data-id="element-726"></td>
                          <td className="border border-black p-3" data-id="element-727"></td>
                          <td className="border border-black p-3" data-id="element-728"></td>
                        </tr>)}
                </tbody>
              </table>
              <div className="p-3 border-t border-gray-300 flex gap-3" data-id="element-729">
                <Button variant="outline" onClick={() => setShowRxModal(true)} className="flex-1 text-green-600 border-green-600 hover:bg-green-50" data-id="element-730">
                  <Pill className="w-4 h-4 mr-2" data-id="element-731" /> Prescribe Medication
                </Button>
                {selectedEncounter.prescriptions.some(rx => rx.status === 'Pending') && <Button onClick={handleSendToPharmacy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-id="element-732">
                    <Pill className="w-4 h-4 mr-2" data-id="element-733" /> Send Prescription to
                    Pharmacy
                  </Button>}
              </div>
            </div>

            {/* FOLLOW UP / REVIEW DATE */}
            <div className="border-2 border-black" data-id="element-734">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-735">
                Next Follow Up / Review Date (If Any)
              </h3>
              <table className="w-full border-collapse text-sm text-center" data-id="element-736">
                <thead data-id="element-737">
                  <tr className="bg-gray-100" data-id="element-738">
                    <th className="border border-black p-1 w-10" data-id="element-739">No.</th>
                    <th className="border border-black p-1" data-id="element-740">Date</th>
                    <th className="border border-black p-1 w-10" data-id="element-741">No.</th>
                    <th className="border border-black p-1" data-id="element-742">Date</th>
                    <th className="border border-black p-1 w-10" data-id="element-743">No.</th>
                    <th className="border border-black p-1" data-id="element-744">Date</th>
                  </tr>
                </thead>
                <tbody data-id="element-745">
                  {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-746">
                      <td className="border border-black p-1 font-medium" data-id="element-747">
                        {row + 1}
                      </td>
                      <td className="border border-black p-1" data-id="element-748">
                        {row === 0 ? <input type="date" className="w-full p-1 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none" value={selectedEncounter.reviewDate ? new Date(selectedEncounter.reviewDate).toISOString().split('T')[0] : ''} onChange={e => {
                      if (e.target.value) {
                        updateEncounter(selectedEncounter.id, {
                          reviewDate: new Date(e.target.value).toISOString()
                        });
                      }
                    }} data-id="element-749" /> : ''}
                      </td>
                      <td className="border border-black p-1 font-medium" data-id="element-750">
                        {row + 6}
                      </td>
                      <td className="border border-black p-2" data-id="element-751"></td>
                      <td className="border border-black p-1 font-medium" data-id="element-752">
                        {row + 11}
                      </td>
                      <td className="border border-black p-2" data-id="element-753"></td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* NAME OF MEDICAL PRACTITIONER */}
            <div className="border-2 border-black p-4 mt-6" data-id="element-754">
              <p className="font-bold text-sm" data-id="element-755">
                Name of the Medical Practitioner:{' '}
                <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-756">
                  {currentUser?.fullName || ''}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* ═══ MODALS ═══ */}
        {/* Lab Modal */}
        {showLabModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-757">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" data-id="element-758">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-759">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-760">
                  Request Laboratory Tests
                </h3>
                <button onClick={() => setShowLabModal(false)} data-id="element-761">
                  <X className="w-5 h-5 text-gray-400" data-id="element-762" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1" data-id="element-763">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-764">
                  <div data-id="element-765">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-766">
                      HAEMATOLOGY
                    </h4>
                    {['Full Blood Count', 'Blood Group', 'CD4'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-767">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-768" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-769">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-770">
                      CHEMISTRY
                    </h4>
                    {['Urea', 'Creatinine', 'FBS', 'RBS'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-771">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-772" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-773">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-774">
                      SEROLOGY
                    </h4>
                    {['HIV', 'Syphilis', 'HBs Ag', 'HCV'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-775">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-776" />{' '}
                        {test}
                      </label>)}
                  </div>
                </div>
                <div className="mt-6" data-id="element-777">
                  <Input label="Other Tests (Free Text)" value={otherTests} onChange={e => setOtherTests(e.target.value)} placeholder="Enter any other tests required..." data-id="element-778" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-779">
                <Button variant="outline" onClick={() => setShowLabModal(false)} data-id="element-780">
                  Cancel
                </Button>
                <Button onClick={handleRequestLab} data-id="element-781">Send to Laboratory</Button>
              </div>
            </div>
          </div>}

        {/* Rad Modal */}
        {showRadModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-782">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-783">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-784">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-785">
                  Request Radiology Scan
                </h3>
                <button onClick={() => setShowRadModal(false)} data-id="element-786">
                  <X className="w-5 h-5 text-gray-400" data-id="element-787" />
                </button>
              </div>
              <div className="p-6" data-id="element-788">
                <Input label="Exact Scan Requested" placeholder="e.g. Pelvic Ultrasound, Abdominal Scan" value={scanType} onChange={e => setScanType(e.target.value)} required data-id="element-789" />
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-790">
                <Button variant="outline" onClick={() => setShowRadModal(false)} data-id="element-791">
                  Cancel
                </Button>
                <Button onClick={handleRequestRad} className="bg-purple-600 hover:bg-purple-700" data-id="element-792">
                  Send to Radiology
                </Button>
              </div>
            </div>
          </div>}

        {/* Rx Modal */}
        {showRxModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-793">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full" data-id="element-794">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-795">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-796">
                  Prescribe Medication
                </h3>
                <button onClick={() => setShowRxModal(false)} data-id="element-797">
                  <X className="w-5 h-5 text-gray-400" data-id="element-798" />
                </button>
              </div>
              <div className="p-6" data-id="element-799">
                <div className="grid grid-cols-2 gap-4" data-id="element-800">
                  <Input label="Drug Name" className="col-span-2" value={rxForm.drugName} onChange={e => setRxForm({
                ...rxForm,
                drugName: e.target.value
              })} required data-id="element-801" />
                  <Input label="Dose" placeholder="e.g. 500mg" value={rxForm.dose} onChange={e => setRxForm({
                ...rxForm,
                dose: e.target.value
              })} required data-id="element-802" />
                  <Select label="Route" options={[{
                value: 'PO',
                label: 'Oral (PO)'
              }, {
                value: 'IV',
                label: 'Intravenous (IV)'
              }, {
                value: 'IM',
                label: 'Intramuscular (IM)'
              }, {
                value: 'Topical',
                label: 'Topical'
              }]} value={rxForm.route} onChange={e => setRxForm({
                ...rxForm,
                route: e.target.value
              })} data-id="element-803" />
                  <Input label="Frequency" placeholder="e.g. BD, TDS" value={rxForm.frequency} onChange={e => setRxForm({
                ...rxForm,
                frequency: e.target.value
              })} required data-id="element-804" />
                  <Input label="No. of Days" type="number" value={rxForm.days} onChange={e => setRxForm({
                ...rxForm,
                days: e.target.value
              })} required data-id="element-805" />
                </div>
                <div className="mt-4 flex justify-end" data-id="element-806">
                  <Button onClick={handleAddRx} variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" data-id="element-807">
                    + Add to Prescription List
                  </Button>
                </div>

                {selectedEncounter.prescriptions.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-808">
                    <h4 className="font-medium text-sm mb-2" data-id="element-809">Current List:</h4>
                    <ul className="space-y-2" data-id="element-810">
                      {selectedEncounter.prescriptions.map(rx => <li key={rx.id} className="text-sm bg-gray-50 p-2 rounded flex justify-between" data-id="element-811">
                          <span data-id="element-812">
                            {rx.drugName} - {rx.dose}
                          </span>
                          <span className="text-gray-500" data-id="element-813">
                            {rx.frequency} x {rx.days} days
                          </span>
                        </li>)}
                    </ul>
                  </div>}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-814">
                <Button onClick={() => setShowRxModal(false)} data-id="element-815">Done</Button>
              </div>
            </div>
          </div>}

        {/* Image Zoom Modal */}
        {zoomImage && <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50" onClick={() => setZoomImage(null)} data-id="element-816">
            <div className="absolute top-4 right-4 flex items-center gap-3 z-10" data-id="element-817">
              <span className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded" data-id="element-818">
                Scroll to zoom • Click outside to close
              </span>
              <button className="text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2" onClick={() => setZoomImage(null)} data-id="element-819">
                <X className="w-6 h-6" data-id="element-820" />
              </button>
            </div>
            <div className="max-w-[90vw] max-h-[85vh] overflow-auto rounded-lg" onClick={e => e.stopPropagation()} data-id="element-821">
              <img src={zoomImage} alt="Radiology Scan — Zoom up to 200%" className="min-w-[60vw] max-w-none" style={{
            width: '180%',
            cursor: 'grab'
          }} data-id="element-822" />
            </div>
            <p className="text-gray-400 text-xs mt-3" data-id="element-823">
              Image displayed at up to 200% zoom. Scroll within the frame to
              pan.
            </p>
          </div>}
      </div>;
  }
  return <div className="space-y-6" data-id="element-824">
      <div className="flex justify-between items-center" data-id="element-825">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-826">
          Clinician Dashboard
        </h1>
        <Badge variant="info" className="text-sm px-3 py-1" data-id="element-827">
          {pendingEncounters.length} Patients Waiting
        </Badge>
      </div>

      <Card noPadding data-id="element-828">
        <ul className="divide-y divide-gray-200" data-id="element-829">
          {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const isWaitingResults = encounter.stage === 'INVESTIGATIONS';
          const hasResults = isWaitingResults && !encounter.pendingLab && !encounter.pendingRadiology;
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-830">
                  <div className="flex items-center" data-id="element-831">
                    <div className={`p-2 rounded-full mr-4 ${hasResults ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-832">
                      <Stethoscope className={`h-6 w-6 ${hasResults ? 'text-green-600' : 'text-blue-600'}`} data-id="element-833" />
                    </div>
                    <div data-id="element-834">
                      <p className="text-sm font-medium text-blue-600" data-id="element-835">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-836">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <div className="flex space-x-2 mt-1" data-id="element-837">
                        <Badge variant="default" data-id="element-838">{patient.age} yrs</Badge>
                        {hasResults && <Badge variant="success" data-id="element-839">Results Ready</Badge>}
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-840">
                    Open File
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-841">
              <Stethoscope className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-842" />
              <p className="text-lg font-medium text-gray-900" data-id="element-843">
                No patients waiting
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};